import { type IconName } from '@dev-spendesk/grapes';
import { useEffect, useMemo, useReducer, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { type SaveAllChanges } from './useAnalyticalFieldMapping';
import {
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingFieldType,
  type AnalyticalFieldMappingV2,
  type InternalValue,
} from '../../bookkeep';
import { type AnalyticalFieldExportSettings } from '../apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from '../apis/useSetSyncSettings';

type ButtonContent = {
  text: string;
  icon?: IconName;
};

type Mode = 'syncSettings' | 'mapping' | 'exportSettings' | 'initial';

const STATE: Record<Mode, number> = {
  initial: -1,
  syncSettings: 0,
  mapping: 1,
  exportSettings: 2,
};

type ErrorType = {
  selectedField: boolean;
  exportSettings: boolean;
  valueMapping: boolean;
};

const reducer = (_state: number, mode: Mode) => {
  return STATE[mode];
};

type Input<TField extends InternalValue, TValue extends InternalValue> = {
  internalField: TField;
  fieldType: AnalyticalFieldMappingFieldType;
  isOpen: boolean;
  isExportConfigEnabled: boolean;
  fieldExportSetting?: AnalyticalFieldExportSettings | undefined;
  isIntegrationNameLoading: boolean;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  values: TValue[];
  syncMode: SyncMode | undefined;
  onClose: () => void;
  onSaveChanges: SaveAllChanges;
  reset: () => void;
};

const InitialErrorState: ErrorType = {
  selectedField: false,
  exportSettings: false,
  valueMapping: false,
};

export const useAnalyticalFieldMappingSetupModal = <
  TField extends InternalValue,
  TValue extends InternalValue,
>({
  isOpen,
  isExportConfigEnabled,
  syncMode,
  fieldExportSetting,
  analyticalFieldMappings,
  internalField,
  values,
  fieldType,
  onClose,
  onSaveChanges,
  reset,
}: Input<TField, TValue>) => {
  const { t } = useTranslation('global');
  const [mode, setMode] = useReducer(reducer, STATE.initial);
  const [fieldMapping, setFieldMapping] = useState<AnalyticalFieldMappingV2>();
  const [selectedSyncMode, setSelectedSyncMode] = useState<SyncMode>('none');
  const [exportSettings, setExportSettings] =
    useState<AnalyticalFieldExportSettings>();

  const [errors, setErrors] = useState<ErrorType>(InitialErrorState);

  const [fieldMappingValues, setFieldMappingValues] = useState<
    AnalyticalFieldValueMappingV2[]
  >([]);

  const isMappingRequired =
    selectedSyncMode !== 'none' &&
    (!fieldMapping?.internalId ||
      fieldMapping?.values.some(({ internalId }) => !internalId));

  useEffect(() => {
    if (syncMode) {
      setSelectedSyncMode(syncMode);
    }
  }, [syncMode]);

  useEffect(() => {
    if (mode === STATE.initial) {
      setFieldMapping(undefined);
      setSelectedSyncMode('none');
      setExportSettings(undefined);
      setErrors(InitialErrorState);
    }
  }, [mode]);

  useEffect(() => {
    if (fieldExportSetting) {
      setExportSettings(fieldExportSetting);
    }
  }, [fieldExportSetting]);

  const handleSyncModeChanged = (value: SyncMode) => {
    setSelectedSyncMode(value);
  };

  useEffect(() => {
    setFieldMapping(
      analyticalFieldMappings.find(
        ({ internalId }) => internalId === internalField.id,
      ),
    );
  }, [analyticalFieldMappings]);

  useEffect(() => {
    if (!isOpen) {
      setMode('initial');
      return;
    }

    if (!syncMode) {
      setMode('syncSettings');
      return;
    }

    if (isMappingRequired) {
      setMode('mapping');
      return;
    }

    if (isExportConfigEnabled) {
      setMode('exportSettings');
    }
  }, [isOpen, syncMode]);

  const nextButton = useMemo((): ButtonContent => {
    const finishSetup = t(
      'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.finishSetup',
    );
    const valueMap = t(
      'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.valueMap',
    );
    const reviewExportSettings = t(
      'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.reviewExportSettings',
    );

    const finish: ButtonContent = {
      text: finishSetup,
    };

    const mapping: ButtonContent = {
      text: valueMap,
      icon: 'arrow-right',
    };

    const reviewConfig: ButtonContent = {
      text: reviewExportSettings,
      icon: 'arrow-right',
    };

    const buttonFromSyncSettings = () => {
      if (isMappingRequired) {
        return mapping;
      }

      if (isExportConfigEnabled) {
        return reviewConfig;
      }

      return finish;
    };

    switch (mode) {
      case STATE.syncSettings:
        return buttonFromSyncSettings();
      case STATE.mapping:
        return isExportConfigEnabled ? reviewConfig : finish;
      default:
        return finish;
    }
  }, [isMappingRequired, isExportConfigEnabled, mode]);

  const backButton = useMemo((): ButtonContent | undefined => {
    const cancel = t('misc.cancel');
    const backToFieldSync = t(
      'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.backButtonSync',
    );
    const backToMapping = t(
      'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.backToMapping',
    );

    const buttonFromExportConfig = (): ButtonContent => {
      if (isMappingRequired) {
        return {
          text: backToMapping,
          icon: 'arrow-left',
        };
      }

      return {
        text: backToFieldSync,
        icon: 'arrow-left',
      };
    };

    switch (mode) {
      case STATE.syncSettings:
        return {
          text: cancel,
        };
      case STATE.mapping:
        return {
          text: backToFieldSync,
          icon: 'arrow-left',
        };
      case STATE.exportSettings:
        return buttonFromExportConfig();
      default:
        return undefined;
    }
  }, [mode]);

  const handleClose = (save: boolean = true) => {
    if (save) {
      saveAll();
    }

    onClose();
    reset();
    setMode('initial');
  };

  const proceedFromSyncSettings = () => {
    if (isMappingRequired) {
      setMode('mapping');
      return;
    }

    if (isExportConfigEnabled) {
      setMode('exportSettings');
      return;
    }

    handleClose();
  };

  const mappedInternalIds = useMemo(
    () =>
      new Set(
        fieldMappingValues
          .filter(({ internalId }) => !!internalId)
          .map(({ internalId }) => internalId),
      ),
    [fieldMappingValues],
  );

  const proceedFromMapping = () => {
    if (
      selectedSyncMode === 'pull' &&
      fieldMapping?.internalId &&
      values.some(({ id }) => !mappedInternalIds.has(id))
    ) {
      setErrors((previous) => ({
        ...previous,
        valueMapping: true,
      }));
      return;
    }

    if (isExportConfigEnabled) {
      setMode('exportSettings');
    } else {
      handleClose();
    }
  };

  const proceedFromExportSettings = () => {
    if (
      isExportConfigEnabled &&
      !exportSettings?.sendOnHeader &&
      !exportSettings?.sendOnLine
    ) {
      setErrors({
        ...errors,
        exportSettings: true,
      });

      return;
    }
    handleClose();
  };

  const saveAll = () => {
    onSaveChanges({
      mapping: fieldMapping
        ? {
            fieldId: fieldMapping.id,
            fieldType: selectedSyncMode === 'none' ? null : fieldType,
            internalId: selectedSyncMode === 'none' ? null : internalField.id,
            values:
              selectedSyncMode === 'none'
                ? fieldMappingValues.map((value) => ({
                    ...value,
                    internalId: null,
                  }))
                : fieldMappingValues,
          }
        : undefined,
      syncMode: selectedSyncMode,
      exportSettings,
    });
  };

  const moveNext = () => {
    switch (mode) {
      case STATE.syncSettings:
        proceedFromSyncSettings();
        break;
      case STATE.mapping:
        proceedFromMapping();
        break;
      case STATE.exportSettings:
        proceedFromExportSettings();
        break;
      default:
        break;
    }
  };

  const moveBack = () => {
    setErrors(InitialErrorState);

    if (mode === STATE.syncSettings) {
      handleClose(false);
      return;
    }

    if (mode === STATE.exportSettings && isMappingRequired) {
      setMode('mapping');
      return;
    }

    setMode('syncSettings');
  };

  const stepTitles = useMemo(
    () => [
      t(
        'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.fieldSync',
      ),
      ...(isMappingRequired
        ? [
            t(
              'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.valueMap',
            ),
          ]
        : []),
      ...(isExportConfigEnabled
        ? [
            t(
              'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.exportSettings',
            ),
          ]
        : []),
    ],
    [isMappingRequired, isExportConfigEnabled, t],
  );

  return {
    moveBack,
    moveNext,
    backButton,
    nextButton,
    handleSyncModeChanged,
    stepTitles,
    mode,
    selectedSyncMode,
    fieldMapping,
    setFieldMapping,
    fieldMappingValues,
    setFieldMappingValues,
    setErrors,
    errors,
    setExportConfig: setExportSettings,
    isSyncSettingsMode: () => mode === STATE.syncSettings,
    isMappingMode: () => mode === STATE.mapping,
    isExportSettingsMode: () => mode === STATE.exportSettings,
  };
};
